// ALA Meet — entry point + screen router (Fase 6 redesign).
// Riusa il motore (signaling + mediasoup-client) invariato.
// Le RPC al server NON cambiano. Cambia solo la presentazione.

import { ensureIconSprite } from './icons.js';
import { setupPWA, triggerInstall } from './pwa.js';
import { setupDebug } from './debug.js';
import { mountLobby, type LobbyResult } from './lobby.js';
import { mountWaiting } from './waiting.js';
import { mountRoom } from './room.js';
import { mountEndCall } from './endcall.js';
import { mountConnectionLost } from './connection-lost.js';

export type Screen = 'lobby' | 'waiting' | 'room' | 'ended' | 'lost';

interface AppState {
  installable: boolean;
}

const state: AppState = { installable: false };

const app = document.getElementById('app')!;

setupDebug();
ensureIconSprite();

setupPWA((canInstall) => {
  state.installable = canInstall;
  renderInstallPill();
});

function renderInstallPill() {
  let pill = document.querySelector<HTMLButtonElement>('.install-pill');
  if (state.installable) {
    if (!pill) {
      pill = document.createElement('button');
      pill.className = 'install-pill';
      pill.innerHTML = `<svg class="icon icon-sm"><use href="#icon-install"/></svg> Installa app`;
      pill.addEventListener('click', async () => {
        await triggerInstall();
        state.installable = false;
        renderInstallPill();
      });
      document.body.appendChild(pill);
    }
  } else if (pill) {
    pill.remove();
  }
}

// ───────────────────────── router ─────────────────────────

async function show(screen: Screen, opts?: unknown): Promise<void> {
  app.innerHTML = '';
  app.dataset.screen = screen;
  switch (screen) {
    case 'lobby': return startLobby();
    case 'waiting': return startWaiting(opts as Parameters<typeof startWaiting>[0]);
    case 'room': return startRoom(opts as Parameters<typeof startRoom>[0]);
    case 'ended': return startEnded(opts as Parameters<typeof startEnded>[0]);
    case 'lost': return startLost(opts as Parameters<typeof startLost>[0]);
  }
}

async function startLobby(): Promise<void> {
  const res = await mountLobby(app);
  // Lobby ha già fatto: WS aperto, join inviato.
  // Se è waiting → mostra waiting screen.
  // Se è ammesso direttamente → entra in stanza.
  if (res.waiting) {
    // CRITICO: micStream/camStream devono attraversare la waiting room, altrimenti
    // dopo l'admit il guest non ha tracce da pubblicare → niente flusso in uscita.
    void show('waiting', {
      signaling: res.signaling,
      joinResp: res.joinResp,
      devices: res.devices,
      prefs: res.prefs,
      micStream: res.micStream,
      camStream: res.camStream,
      authToken: res.authToken,
      roomId: res.roomId,
      apiOrigin: res.apiOrigin,
      roomLabel: res.roomLabel,
    });
    return;
  }
  void show('room', {
    signaling: res.signaling,
    joinResp: res.joinResp,
    devices: res.devices,
    prefs: res.prefs,
    roomLabel: res.roomLabel,
    micStream: res.micStream,
    camStream: res.camStream,
    authToken: res.authToken,
    roomId: res.roomId,
    apiOrigin: res.apiOrigin,
  });
}

async function startWaiting(opts: Parameters<typeof mountWaiting>[1]): Promise<void> {
  await mountWaiting(app, opts, {
    onAdmitted: (admitPayload) => {
      // Patch del joinResp con i dati ricevuti dall'admit per usarli in room.ts
      // come se fossero arrivati alla join (router caps + peers + producers + recording).
      const patchedJoinResp = {
        ...opts.joinResp,
        routerRtpCapabilities: (admitPayload.routerRtpCapabilities ?? opts.joinResp.routerRtpCapabilities) as typeof opts.joinResp.routerRtpCapabilities,
        peers: admitPayload.peers ?? opts.joinResp.peers,
        existingProducers: (admitPayload.existingProducers ?? opts.joinResp.existingProducers) as typeof opts.joinResp.existingProducers,
        recording: admitPayload.recording ?? opts.joinResp.recording,
        existingFiles: admitPayload.existingFiles ?? opts.joinResp.existingFiles,
      };
      void show('room', {
        signaling: opts.signaling,
        joinResp: patchedJoinResp,
        devices: opts.devices,
        prefs: opts.prefs,
        roomLabel: opts.joinResp.roomLabel ?? 'Riunione',
        micStream: opts.micStream,
        camStream: opts.camStream,
        wasGuest: true,
        authToken: opts.authToken,
        roomId: opts.roomId,
        apiOrigin: opts.apiOrigin,
      });
    },
    onRejected: (reason) => {
      void show('lobby');
      const err = document.querySelector<HTMLDivElement>('.lobby-error');
      if (err) err.textContent = reason || 'Sei stato rifiutato dall\'host.';
    },
    onCancel: () => { try { opts.signaling.close(); } catch { /* */ } void show('lobby'); },
    onConnectionLost: () => void show('lost', { signaling: opts.signaling }),
  });
}

async function startRoom(opts: Parameters<typeof mountRoom>[1]): Promise<void> {
  await mountRoom(app, opts, {
    onEnded: (summary) => void show('ended', { summary, roomLabel: opts.roomLabel }),
    onConnectionLost: () => void show('lost', { signaling: opts.signaling }),
  });
}

async function startEnded(opts: Parameters<typeof mountEndCall>[1]): Promise<void> {
  await mountEndCall(app, opts, {
    onNew: () => void show('lobby'),
  });
}

async function startLost(opts: Parameters<typeof mountConnectionLost>[1]): Promise<void> {
  await mountConnectionLost(app, opts, {
    onRecovered: () => void show('lobby'),
    onGiveUp: () => void show('lobby'),
  });
}

// boot
void show('lobby');

// expose dev API
declare global {
  interface Window { __ALA_MEET__?: unknown; }
}
window.__ALA_MEET__ = { show, state };
